'use client';

export const DECISION_RETURN_MIN_MS = 30 * 60 * 1000; // 30 minutos
export const DECISION_RETURN_MAX_MS = 14 * 24 * 60 * 60 * 1000; // 14 días
export const DECISION_STORAGE_PREFIX = 'pa.dv.';
export const RETURN_VISIT_BANNER_STORAGE_KEY = 'pa.return_visit_banner';
export const RETURN_VISIT_BANNER_COOKIE = 'pa_return_visit_banner';
export const RETURN_VISIT_BANNER_EVENT = 'pa:return-visit-detected';

export type DecisionTargetType =
  | 'apartment_detail'
  | 'apartment_neighborhood'
  | 'building'
  | 'reservas';

export interface DecisionTarget {
  type: DecisionTargetType;
  /** Path canónico para reportar como `target_path`. Sin query/hash. */
  path: string;
  /** Llave normalizada para localStorage — diferencia búsquedas con scope distinto en /reservas. */
  key: string;
}

export interface DecisionStoredVisit {
  count: number;
  lastSeenAt: string;
}

type URLSearchParamsLike = Pick<URLSearchParams, 'get'>;

export interface ReturnVisitBannerPayload {
  targetPath: string;
  targetType: DecisionTargetType;
  targetKey: string;
  visitCount: number;
  previousSeenAt: string;
  detectedAt: string;
  daysSincePrevious: number;
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
}

export function getDecisionTarget(
  pathname: string,
  searchParams: URLSearchParamsLike | null
): DecisionTarget | null {
  if (pathname === '/reservas') {
    const building = searchParams?.get('building') ?? '';
    const neighborhood = searchParams?.get('neighborhood') ?? '';
    return {
      type: 'reservas',
      path: '/reservas',
      key: `reservas|building=${building}|neighborhood=${neighborhood}`,
    };
  }

  if (pathname === '/apartamentos/el-laguito') {
    return {
      type: 'apartment_neighborhood',
      path: pathname,
      key: pathname,
    };
  }

  if (pathname.startsWith('/apartamentos/') && pathname !== '/apartamentos') {
    return {
      type: 'apartment_detail',
      path: pathname,
      key: pathname,
    };
  }

  if (pathname.startsWith('/edificios/')) {
    return {
      type: 'building',
      path: pathname,
      key: pathname,
    };
  }

  return null;
}

export function getDecisionStorageKey(key: string): string {
  return `${DECISION_STORAGE_PREFIX}${key}`;
}

export function readDecisionStored(key: string): DecisionStoredVisit | null {
  try {
    const raw = localStorage.getItem(getDecisionStorageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.count === 'number' &&
      typeof parsed.lastSeenAt === 'string'
    ) {
      return parsed as DecisionStoredVisit;
    }
    return null;
  } catch {
    return null;
  }
}

export function writeDecisionStored(key: string, value: DecisionStoredVisit): void {
  try {
    localStorage.setItem(getDecisionStorageKey(key), JSON.stringify(value));
  } catch {
    /* localStorage puede fallar (quota/private mode) — tracking es best-effort */
  }
}

export function buildReturnVisitBannerPayload(
  target: DecisionTarget,
  stored: DecisionStoredVisit,
  nextCount: number,
  detectedAt: Date,
  diffMs: number
): ReturnVisitBannerPayload {
  const daysSincePrevious = Math.round((diffMs / (24 * 60 * 60 * 1000)) * 10) / 10;
  const copy = getReturnVisitCopy(target);

  return {
    targetPath: target.path,
    targetType: target.type,
    targetKey: target.key,
    visitCount: nextCount,
    previousSeenAt: stored.lastSeenAt,
    detectedAt: detectedAt.toISOString(),
    daysSincePrevious,
    ...copy,
  };
}

export function getInferredReturnVisitPayload(
  pathname: string,
  searchParams: URLSearchParamsLike | null,
  now = new Date()
): ReturnVisitBannerPayload | null {
  const target = getDecisionTarget(pathname, searchParams);
  if (!target) return null;

  const stored = readDecisionStored(target.key);
  if (!stored?.lastSeenAt) return null;

  const prevMs = Date.parse(stored.lastSeenAt);
  if (Number.isNaN(prevMs)) return null;

  const diffMs = now.getTime() - prevMs;
  if (diffMs < DECISION_RETURN_MIN_MS || diffMs > DECISION_RETURN_MAX_MS) return null;

  return buildReturnVisitBannerPayload(target, stored, stored.count + 1, now, diffMs);
}

export function persistReturnVisitBannerPayload(payload: ReturnVisitBannerPayload): void {
  try {
    localStorage.setItem(RETURN_VISIT_BANNER_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* Persistencia opcional; el banner también recibe el evento en memoria. */
  }

  try {
    document.cookie = `${RETURN_VISIT_BANNER_COOKIE}=${encodeURIComponent(
      JSON.stringify(payload)
    )}; Path=/; Max-Age=604800; SameSite=Lax`;
  } catch {
    /* document.cookie puede estar bloqueado; no debe afectar navegación. */
  }
}

export function readPersistedReturnVisitBannerPayload(): ReturnVisitBannerPayload | null {
  const fromStorage = readPayloadFromStorage();
  if (fromStorage) return fromStorage;
  return readPayloadFromCookie();
}

export function isReturnVisitPayloadFresh(
  payload: ReturnVisitBannerPayload,
  now = new Date()
): boolean {
  const detectedMs = Date.parse(payload.detectedAt);
  if (Number.isNaN(detectedMs)) return false;
  return now.getTime() - detectedMs <= 7 * 24 * 60 * 60 * 1000;
}

export function getReturnVisitDismissedKey(targetKey: string): string {
  return `pa.return_visit_dismissed.${targetKey}`;
}

function readPayloadFromStorage(): ReturnVisitBannerPayload | null {
  try {
    const raw = localStorage.getItem(RETURN_VISIT_BANNER_STORAGE_KEY);
    return parsePayload(raw);
  } catch {
    return null;
  }
}

function readPayloadFromCookie(): ReturnVisitBannerPayload | null {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${RETURN_VISIT_BANNER_COOKIE}=([^;]+)`)
    );
    if (!match) return null;
    return parsePayload(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

function parsePayload(raw: string | null): ReturnVisitBannerPayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.targetPath === 'string' &&
      typeof parsed.targetType === 'string' &&
      typeof parsed.targetKey === 'string' &&
      typeof parsed.visitCount === 'number' &&
      typeof parsed.previousSeenAt === 'string' &&
      typeof parsed.detectedAt === 'string' &&
      typeof parsed.daysSincePrevious === 'number' &&
      typeof parsed.title === 'string' &&
      typeof parsed.body === 'string' &&
      typeof parsed.ctaLabel === 'string' &&
      typeof parsed.ctaHref === 'string'
    ) {
      return parsed as ReturnVisitBannerPayload;
    }
    return null;
  } catch {
    return null;
  }
}

function getReturnVisitCopy(target: DecisionTarget): Pick<
  ReturnVisitBannerPayload,
  'title' | 'body' | 'ctaLabel' | 'ctaHref'
> {
  if (target.type === 'building') {
    const name = getReadableTargetName(target.path, 'Edificio');
    return {
      title: 'Bienvenido de nuevo',
      body: `Continúa con ${name} y revisa fechas disponibles cuando estés listo.`,
      ctaLabel: 'Ver disponibilidad',
      ctaHref: `${target.path}#consultar-disponibilidad`,
    };
  }

  if (target.type === 'apartment_detail') {
    return {
      title: 'Bienvenido de nuevo',
      body: 'Continúa con el apartamento que estabas viendo.',
      ctaLabel: 'Volver al apartamento',
      ctaHref: target.path,
    };
  }

  if (target.type === 'apartment_neighborhood') {
    return {
      title: 'Bienvenido de nuevo',
      body: 'Retoma los apartamentos por días en El Laguito.',
      ctaLabel: 'Ver opciones',
      ctaHref: target.path,
    };
  }

  return {
    title: 'Bienvenido de nuevo',
    body: 'Retoma tu búsqueda de fechas y huéspedes.',
    ctaLabel: 'Continuar búsqueda',
    ctaHref: getReservasHrefFromKey(target.key),
  };
}

function getReservasHrefFromKey(key: string): string {
  const params = new URLSearchParams();
  const building = key.match(/(?:^|\|)building=([^|]*)/)?.[1] ?? '';
  const neighborhood = key.match(/(?:^|\|)neighborhood=([^|]*)/)?.[1] ?? '';
  if (building) params.set('building', building);
  if (neighborhood) params.set('neighborhood', neighborhood);
  params.set('source', 'return_visit_banner');
  return `/reservas?${params.toString()}`;
}

function getReadableTargetName(path: string, prefix: string): string {
  const slug = path.split('/').filter(Boolean).at(-1) ?? '';
  if (slug === 'nuevo-conquistador') return 'Edificio Nuevo Conquistador';
  const label = slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  return label ? `${prefix} ${label}` : prefix;
}
